/** Browser file helpers for uploads and downloads (both apps). */

const IMAGE_TYPES: Readonly<Record<string, string>> = {
  svg: 'image/svg+xml',
  png: 'image/png',
  webp: 'image/webp',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
};

/** The file's image type; falls back to the extension (some systems report SVGs without a type). */
export function imageTypeOf(file: File): string {
  if (file.type) return file.type;
  return IMAGE_TYPES[file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase()] ?? 'application/octet-stream';
}

/** The file content as base64 (for JSON uploads). */
export async function readFileBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(binary);
}

/** Offers `text` as a file download (e.g. a PEM certificate request). */
export function downloadText(filename: string, text: string, type = 'text/plain'): void {
  downloadBlob(filename, new Blob([text], { type }));
}

/** Binary attachments use download disposition and an inert content type. */
export function downloadBytes(filename: string, bytes: Uint8Array): void {
  downloadBlob(filename, new Blob([new Uint8Array(bytes)], { type: 'application/octet-stream' }));
}

function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Offers `data` as a pretty-printed JSON file download. */
export function downloadJson(filename: string, data: unknown): void {
  downloadText(filename, JSON.stringify(data, null, 2) + '\n', 'application/json');
}
